'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';
function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} data-sentry-element="unknown" data-sentry-component="Dialog" data-sentry-source-file="dialog.tsx" />;
}
function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} data-sentry-element="unknown" data-sentry-component="DialogTrigger" data-sentry-source-file="dialog.tsx" />;
}
function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} data-sentry-element="unknown" data-sentry-component="DialogPortal" data-sentry-source-file="dialog.tsx" />;
}
function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} data-sentry-element="unknown" data-sentry-component="DialogClose" data-sentry-source-file="dialog.tsx" />;
}
function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return <DialogPrimitive.Overlay data-slot="dialog-overlay" className={cn('fixed inset-0 z-50 bg-pl-gray-400 mix-blend-multiply transition-all data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out dark:bg-zinc-950/80', className)} {...props} data-sentry-element="unknown" data-sentry-component="DialogOverlay" data-sentry-source-file="dialog.tsx" />;
}
function DialogContent({
  className,
  compact,
  children,
  showCloseButton = true,
  closeClassName = '',
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  compact?: boolean;
  closeClassName?: string;
}) {
  return <DialogPortal data-slot="dialog-portal" data-sentry-element="DialogPortal" data-sentry-component="DialogContent" data-sentry-source-file="dialog.tsx">
      <DialogOverlay data-sentry-element="DialogOverlay" data-sentry-source-file="dialog.tsx" />
      <DialogPrimitive.Content data-slot="dialog-content" className={cn('fixed flex flex-col inset-0 z-50 lg:inset-[unset] lg:left-[50%] lg:top-[50%] w-full lg:max-w-lg lg:translate-x-[-50%] lg:translate-y-[-50%] lg:border lg:border-zinc-200 bg-white lg:shadow-lg transition-all data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out lg:data-[state=closed]:zoom-out-95 lg:data-[state=open]:zoom-in-95 lg:rounded-lg md:w-full dark:border-zinc-800 dark:bg-zinc-950 rounded-xl overflow-hidden', className, {
      'p-4 pt-2 lg:pt-4 lg:p-6': !compact
    })} {...props} data-sentry-element="unknown" data-sentry-source-file="dialog.tsx">
        {children}
        {showCloseButton && <DialogPrimitive.Close data-slot="dialog-close" className={cn('absolute right-3 lg:right-5 top-3 lg:top-5 rounded-xs opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-zinc-100 data-[state=open]:text-zinc-500 dark:ring-offset-zinc-950 dark:focus:ring-zinc-300 dark:data-[state=open]:bg-zinc-800 dark:data-[state=open]:text-zinc-400', {
        'p-2.5': compact
      }, closeClassName)}>
            <X size={compact ? 16 : 28} />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>}
      </DialogPrimitive.Content>
    </DialogPortal>;
}
function DialogHeader({
  className,
  compact,
  ...props
}: React.ComponentProps<'div'> & {
  compact?: boolean;
}) {
  return <div data-slot="dialog-header" className={cn('flex shrink-0 items-center lg:h-auto border-zinc-200 border-b lg:border-b-none bg-white pb-4 lg:pb-6', {
    'p-4 lg:p-6': compact
  }, className)} {...props} data-sentry-component="DialogHeader" data-sentry-source-file="dialog.tsx" />;
}
function DialogFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-footer" className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} data-sentry-component="DialogFooter" data-sentry-source-file="dialog.tsx" />;
}
function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title data-slot="dialog-title" className={cn('text-xl lg:text-2xl font-bold leading-none tracking-tight', className)} {...props} data-sentry-element="unknown" data-sentry-component="DialogTitle" data-sentry-source-file="dialog.tsx" />;
}
function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description data-slot="dialog-description" className={cn('text-sm text-zinc-500 dark:text-zinc-400', className)} {...props} data-sentry-element="unknown" data-sentry-component="DialogDescription" data-sentry-source-file="dialog.tsx" />;
}
export { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogOverlay, DialogPortal, DialogTitle, DialogDescription, DialogClose };